import {logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {showSearchAtom} from 'atoms/search';
import {Employee} from 'client';
import {handOffSpeculativeRun} from 'components/ai/speculativeAsk';
import {buildRecentSearchHref, recentSearchAtom} from 'components/search/GlobalSearch';
import {useAtom, useSetAtom} from 'jotai';
import {useNavigate} from 'react-router';

import {SearchRow} from './searchRows';

/**
 * What the box knows when something is picked, for SEARCH_SELECTED: the same
 * fields today's box sends, so the two compare, and `onPicked` for the search
 * session. Null: no logging and no recents.
 */
type SearchSelectionLog = {
  input: string;
  results: number;
  resultsLoaded: boolean;
  filter: string;
  onPicked?: (row: SearchRow, index?: number) => void;
};

/** SEARCH_SELECTED's `route` and the recent-search entry for a row, where it has one. */
const destination = (row: SearchRow) => {
  switch (row.kind) {
    case 'person':
      return {route: 'people', path: row.person.ldap ?? '', label: row.person.name};
    case 'team':
      return {route: 'teams', path: row.team.slug ?? '', label: row.team.name ?? ''};
    case 'workstream':
      return {route: 'workstream', path: String(row.workstream.id), label: row.workstream.name};
    case 'news':
      return {route: 'news', path: String(row.post.id), label: row.post.title};
    case 'knowledge':
      return {route: 'knowledge', path: row.result.id, label: row.result.title};
    default:
      return null;
  }
};

/** What picking things does: go there (closing search), or ask. */
export const useSearchActions = (log: SearchSelectionLog | null) => {
  const navigate = useNavigate();
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const [recentSearches, setRecentSearches] = useAtom(recentSearchAtom);

  /** Go there, closing search; a new tab (a ⌘-click) leaves the box open for the next one. */
  const go = (href: string, newTab = false) => {
    if (!newTab) setShowSearch(false);
    if (newTab || /^https?:\/\//.test(href)) window.open(href, '_blank', 'noopener,noreferrer');
    else navigate(href, {state: {source: 'search'}});
  };

  /** Log the pick and remember it, the way today's box does. */
  const record = (row: SearchRow, index?: number) => {
    const to = destination(row);
    if (!log || !to) return;
    if (to.route !== 'knowledge') {
      const href = buildRecentSearchHref({route: to.route, path: to.path}) ?? undefined;
      setRecentSearches(
        [
          {...to, href},
          ...recentSearches.filter(({route, path}) => !(route === to.route && path === to.path)),
        ].slice(0, 8)
      );
    }
    analyticsLogger().logEvent('SEARCH_SELECTED', {
      route: to.route,
      filter: log.filter,
      results: log.results,
      input: log.input.length,
      index,
      results_loaded: log.resultsLoaded,
      search_box: 'relaunch',
    });
    log.onPicked?.(row, index);
  };

  const openPerson = (person: Employee, index?: number, newTab = false) => {
    record({kind: 'person', id: `person:${person.ldap}`, person}, index);
    go(`/people/${person.ldap}`, newTab);
  };

  /** Go to a row; `newTab` only for the ones that are a page (not meetings or asks). */
  const activate = (row: SearchRow, index?: number, newTab = false) => {
    switch (row.kind) {
      case 'person':
        return openPerson(row.person, index, newTab);
      case 'team':
        record(row, index);
        return go(`/teams/${row.team.slug}`, newTab);
      case 'workstream':
        record(row, index);
        return go(`/workstreams/${row.workstream.id}`, newTab);
      case 'news':
        record(row, index);
        return go(`/news/${row.post.id}`, newTab);
      case 'knowledge':
        record(row, index);
        return go(row.result.url, newTab);
      case 'meeting':
        return undefined;
      case 'askSuggestion':
        if (log) {
          logAiSearchInteraction({
            action: 'suggestion_click',
            source: 'search',
            suggestionIndex: index ?? 0,
            suggestionKind: 'company',
          });
        }
        // The pane's answer may already be running: the drawer carries it on.
        handOffSpeculativeRun(row.question);
        return openAiSearchAndAsk(row.question, 'search_suggestion');
      case 'ask':
        handOffSpeculativeRun(row.question);
        return openAiSearchAndAsk(row.question, 'search_typeahead_row');
    }
  };

  return {openPerson, activate};
};
